import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Cliente, Requester, SlaPack } from '../services/api';
import {
  ClienteHasHistoryError,
  createCliente,
  deleteCliente,
  listClientes,
  listRequesters,
  listSlaPacks,
  updateCliente,
} from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { PersonaModal } from '../components/PersonaModal';
import { PersonasTabla } from '../components/PersonasTabla';
import styles from './Mantenimiento.module.css';

/**
 * CLIENTES (paso 85a): la empresa a la que Adaptia da soporte, y sus personas,
 * que se dan de alta AQUÍ (con el formulario único de persona).
 *
 * ⚠️ Un cliente no es un Equipo: `Area` es un equipo INTERNO de Adaptia.
 *
 * Permisos (decisión de Jose, D1): la ficha —crear, editar, desactivar,
 * borrar— es solo de admin (`clientes:manage`); el técnico la ve y gestiona
 * sus personas. Desactivar un cliente corta el acceso a TODAS sus personas, y
 * sus tickets abiertos siguen en la bandeja (D3): por eso se avisa antes.
 *
 * El PACK DE SLA (paso 89) se elige en la ficha y pide además `sla:manage`
 * (D1: es parte del contrato). Quien no lo tiene lo VE en la ficha, no lo
 * cambia. Cambiarlo afecta a los tickets NUEVOS: el pack se copia al ticket.
 */
/** La ficha mientras se edita: todo texto; lo vacío se manda como null. */
type FichaForm = {
  nombre: string;
  cif: string;
  contactoNombre: string;
  telefono: string;
  email: string;
  notas: string;
  /** '' = el estándar (null en la API). */
  slaPackId: string;
};

export function ClientesScreen() {
  const { provider } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canManage = hasPermission('clientes:manage');
  const canAddPersona = hasAnyPermission('users:manage', 'users:create_requester');
  const canManagePacks = hasPermission('sla:manage');
  const [packs, setPacks] = useState<SlaPack[]>([]);

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [q, setQ] = useState('');

  // Vista de un cliente (ficha + personas). El admin no tiene router: es estado.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [personas, setPersonas] = useState<Requester[]>([]);
  const [addingPersona, setAddingPersona] = useState(false);

  // Ficha
  const [fichaOpen, setFichaOpen] = useState(false);
  const [editing, setEditing] = useState<Cliente | null>(null);
  const [form, setForm] = useState<FichaForm>({
    nombre: '', cif: '', contactoNombre: '', telefono: '', email: '', notas: '', slaPackId: '',
  });
  const [saving, setSaving] = useState(false);

  // Desactivar / borrar
  const [confirmOff, setConfirmOff] = useState<Cliente | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Cliente | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  const selected = clientes.find((c) => c.id === selectedId) ?? null;

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      setClientes(await listClientes(token));
      if (selectedId) setPersonas(await listRequesters(token, selectedId));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider, selectedId]);

  useEffect(() => { load(); }, [load]);

  // Los packs, para el selector de la ficha. Solo quien puede asignarlos.
  useEffect(() => {
    if (!canManagePacks) return;
    (async () => {
      const token = await provider.getAccessToken();
      if (token) setPacks(await listSlaPacks(token).catch(() => []));
    })();
  }, [canManagePacks, provider]);
  const estandar = packs.find((p) => p.esEstandar);

  const visibles = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clientes;
    return clientes.filter((c) =>
      `${c.nombre} ${c.cif ?? ''} ${c.contactoNombre ?? ''} ${c.email ?? ''}`.toLowerCase().includes(needle));
  }, [clientes, q]);

  const openFicha = (c: Cliente | null) => {
    setEditing(c);
    setForm({
      nombre: c?.nombre ?? '',
      cif: c?.cif ?? '',
      contactoNombre: c?.contactoNombre ?? '',
      telefono: c?.telefono ?? '',
      email: c?.email ?? '',
      notas: c?.notas ?? '',
      slaPackId: c?.slaPackId ?? '',
    });
    setError(null);
    setFichaOpen(true);
  };

  const saveFicha = async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    // Vacío → null: borrar el CIF mal puesto tiene que poder hacerse, y
    // `EmailStr` rechaza la cadena vacía.
    const payload = {
      nombre: form.nombre.trim(),
      cif: form.cif.trim() || null,
      contactoNombre: form.contactoNombre.trim() || null,
      telefono: form.telefono.trim() || null,
      email: form.email.trim() || null,
      notas: form.notas.trim() || null,
      // Sin `sla:manage` no se manda: el backend lo rechazaría aunque fuera
      // el mismo valor que ya tiene.
      ...(canManagePacks ? { slaPackId: form.slaPackId || null } : {}),
    };
    try {
      if (editing) await updateCliente(editing.id, payload, token);
      else await createCliente(payload, token);
      setFichaOpen(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (c: Cliente, isActive: boolean) => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await updateCliente(c.id, { isActive }, token);
      setConfirmOff(null);
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCliente(deleteTarget.id, token);
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      if (e instanceof ClienteHasHistoryError) setDeleteBlocked(true);
      else setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const field = (k: keyof typeof form) => ({
    value: form[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  const accionesFicha = (c: Cliente) => canManage && (
    <>
      <button className={styles.editBtn} onClick={() => openFicha(c)}>Editar</button>
      {c.isActive ? (
        <button className={styles.deactivateBtn} onClick={() => { setError(null); setConfirmOff(c); }}>Desactivar</button>
      ) : (
        <button className={styles.editBtn} onClick={() => setActive(c, true)}>Reactivar</button>
      )}
      <button className={styles.deactivateBtn} onClick={() => { setError(null); setDeleteBlocked(false); setDeleteTarget(c); }}>Borrar</button>
    </>
  );

  if (loading) return <div className={styles.loading}>Cargando clientes…</div>;

  return (
    <div className={styles.container}>
      {selected ? (
        <>
          <button className={styles.back} onClick={() => { setSelectedId(null); setNotice(null); }}>← Clientes</button>
          <div className={styles.header}>
            <h2 className={styles.title}>
              {selected.nombre}{' '}
              {!selected.isActive && <span className={styles.badgeInactive}>Desactivado</span>}
            </h2>
            <div className={styles.headerActions}>{accionesFicha(selected)}</div>
          </div>
          {error && !fichaOpen && !confirmOff && !deleteTarget && <div className={styles.error} style={{ margin: '0 0 12px' }}>{error}</div>}
          <div className={styles.ficha}>
            <div className={styles.fichaItem}><span className={styles.fichaLabel}>CIF</span>{selected.cif || '—'}</div>
            <div className={styles.fichaItem}><span className={styles.fichaLabel}>Contacto</span>{selected.contactoNombre || '—'}</div>
            <div className={styles.fichaItem}><span className={styles.fichaLabel}>Teléfono</span>{selected.telefono || '—'}</div>
            <div className={styles.fichaItem}><span className={styles.fichaLabel}>Email</span>{selected.email || '—'}</div>
            <div className={styles.fichaItem}><span className={styles.fichaLabel}>Tickets abiertos</span><span className={styles.num}>{selected.ticketsAbiertos}</span></div>
            <div className={styles.fichaItem}><span className={styles.fichaLabel}>SLA</span>{selected.slaPackNombre || '—'}</div>
            {selected.notas && (
              <div className={`${styles.fichaItem} ${styles.fichaNotas}`}><span className={styles.fichaLabel}>Notas</span>{selected.notas}</div>
            )}
          </div>

          <div className={styles.header}>
            <h3 className={styles.subTitle}>Personas ({personas.length})</h3>
            {canAddPersona && selected.isActive && (
              <button className={styles.createBtn} onClick={() => { setAddingPersona(true); setNotice(null); }}>+ Nueva persona</button>
            )}
          </div>
          {notice && <div className={styles.success}>{notice}</div>}
          <PersonasTabla personas={personas} showCliente={false} onChanged={load} onNotice={setNotice} />
        </>
      ) : (
        <>
          <div className={styles.header}>
            <h2 className={styles.title}>Clientes</h2>
            {canManage && <button className={styles.createBtn} onClick={() => openFicha(null)}>+ Nuevo cliente</button>}
          </div>
          {error && !fichaOpen && !confirmOff && !deleteTarget && <div className={styles.error} style={{ margin: '0 0 12px' }}>{error}</div>}
          <div className={styles.toolbar}>
            <input
              className={styles.input}
              type="search"
              placeholder="Buscar por nombre, CIF o contacto"
              aria-label="Buscar clientes"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span className={styles.count}>{visibles.length} de {clientes.length}</span>
          </div>
          <div className={styles.tableCard}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Contacto</th>
                  <th>Personas</th>
                  <th>Abiertos</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((c) => (
                  <tr key={c.id} className={!c.isActive ? styles.inactive : ''}>
                    <td>
                      <button className={styles.linkBtn} onClick={() => { setSelectedId(c.id); setNotice(null); }}>{c.nombre}</button>
                      {c.cif && <span className={styles.sub}>{c.cif}</span>}
                    </td>
                    <td>
                      {c.contactoNombre || '—'}
                      {(c.email || c.telefono) && <span className={styles.sub}>{[c.email, c.telefono].filter(Boolean).join(' · ')}</span>}
                    </td>
                    <td className={styles.num}>{c.personas}</td>
                    <td className={styles.num}>{c.ticketsAbiertos}</td>
                    <td>
                      <span className={c.isActive ? styles.badgeActive : styles.badgeInactive}>
                        {c.isActive ? 'Activo' : 'Desactivado'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                      <button className={styles.editBtn} onClick={() => { setSelectedId(c.id); setNotice(null); }}>Personas</button>
                      {accionesFicha(c)}
                      </div>
                    </td>
                  </tr>
                ))}
                {visibles.length === 0 && (
                  <tr><td colSpan={6} className={styles.empty}>{clientes.length ? 'Ningún cliente coincide' : 'No hay clientes'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {addingPersona && selected && (
        <PersonaModal
          fixedCliente={{ id: selected.id, nombre: selected.nombre }}
          onClose={() => setAddingPersona(false)}
          onSaved={async (p, { accessSent }) => {
            setAddingPersona(false);
            setNotice(accessSent
              ? `${p.email} ya tiene acceso: le hemos enviado su enlace de entrada al portal.`
              : `${p.email} dado de alta sin enviarle el acceso. Mándaselo con «Enviar acceso» cuando toque.`);
            await load();
          }}
        />
      )}

      {/* ── Ficha ─────────────────────────────────────────── */}
      {fichaOpen && (
        <div className={styles.backdrop} onClick={() => setFichaOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={editing ? 'Editar cliente' : 'Nuevo cliente'}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{editing ? 'Editar cliente' : 'Nuevo cliente'}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setFichaOpen(false)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="cl-nombre">Nombre</label>
                <input id="cl-nombre" className={styles.input} {...field('nombre')} />
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="cl-cif">CIF</label>
                  <input id="cl-cif" className={styles.input} {...field('cif')} />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="cl-tel">Teléfono</label>
                  <input id="cl-tel" className={styles.input} type="tel" {...field('telefono')} />
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="cl-contacto">Persona de contacto</label>
                <input id="cl-contacto" className={styles.input} {...field('contactoNombre')} />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="cl-email">Email de contacto</label>
                <input id="cl-email" className={styles.input} type="email" {...field('email')} />
                <span className={styles.muted}>Solo para tener el dato: no da acceso a nadie. El acceso es por persona.</span>
              </div>
              {canManagePacks && (
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="cl-sla">SLA</label>
                  <select
                    id="cl-sla"
                    className={styles.select}
                    value={form.slaPackId}
                    onChange={(e) => setForm((f) => ({ ...f, slaPackId: e.target.value }))}
                  >
                    <option value="">{estandar ? `${estandar.nombre} (el de todos)` : 'Estándar'}</option>
                    {packs
                      .filter((p) => !p.esEstandar && (p.isActive || p.id === form.slaPackId))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre}{p.isActive ? '' : ' (desactivado)'}
                        </option>
                      ))}
                  </select>
                  <span className={styles.muted}>Cuenta para los tickets que entren a partir de ahora: los abiertos conservan su plazo.</span>
                </div>
              )}
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="cl-notas">Notas</label>
                <textarea id="cl-notas" className={styles.textarea} {...field('notas')} />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setFichaOpen(false)}>Cancelar</button>
              <button className={styles.saveBtn} onClick={saveFicha} disabled={saving || !form.nombre.trim()}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Desactivar (D3: solo aviso) ───────────────────── */}
      {confirmOff && (
        <div className={styles.backdrop} onClick={() => setConfirmOff(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Desactivar cliente">
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Desactivar {confirmOff.nombre}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setConfirmOff(null)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              <p style={{ margin: 0 }}>
                {confirmOff.personas === 1 ? 'Su persona deja' : `Sus ${confirmOff.personas} personas dejan`} de poder
                entrar al portal y de abrir o contestar tickets por correo.
              </p>
              {confirmOff.ticketsAbiertos > 0 && (
                <div className={styles.notice}>
                  Tiene <strong>{confirmOff.ticketsAbiertos} {confirmOff.ticketsAbiertos === 1 ? 'ticket abierto' : 'tickets abiertos'}</strong>.
                  Siguen en la bandeja y se pueden seguir trabajando, pero el cliente ya no podrá contestar.
                </div>
              )}
              <p className={styles.muted} style={{ margin: 0 }}>
                Al reactivarlo vuelven a entrar todas, salvo las que estén desactivadas una a una.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setConfirmOff(null)}>Cancelar</button>
              <button className={styles.dangerBtn} onClick={() => setActive(confirmOff, false)} disabled={saving}>
                {saving ? 'Desactivando…' : 'Desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Borrar (solo sin rastro) ──────────────────────── */}
      {deleteTarget && (
        <div className={styles.backdrop} onClick={() => setDeleteTarget(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Borrar cliente">
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{deleteBlocked ? 'No se puede borrar' : `Borrar ${deleteTarget.nombre}`}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setDeleteTarget(null)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              {deleteBlocked ? (
                <p style={{ margin: 0 }}>
                  <strong>{deleteTarget.nombre}</strong> tiene personas o tickets, así que no se puede
                  borrar: se perdería a quién pertenecían. {deleteTarget.isActive
                    ? 'Desactívalo en su lugar.'
                    : 'Ya está desactivado.'}
                </p>
              ) : (
                <p style={{ margin: 0 }}>
                  Solo se puede borrar un cliente dado de alta por error, sin personas ni tickets.
                  Es <strong>irreversible</strong>.
                </p>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setDeleteTarget(null)}>Cancelar</button>
              {deleteBlocked ? (
                deleteTarget.isActive && (
                  <button className={styles.dangerBtn} onClick={() => { const c = deleteTarget; setDeleteTarget(null); setConfirmOff(c); }}>
                    Desactivar…
                  </button>
                )
              ) : (
                <button className={styles.dangerBtn} onClick={confirmDelete} disabled={saving}>
                  {saving ? 'Borrando…' : 'Borrar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
